import { CommonModule } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  input,
  signal,
  ViewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import DOMPurify from 'dompurify';

export type ChatMessageStatus =
  | 'sending'
  | 'read'
  | 'sent'
  | 'failed'
  | 'delivered'
  | 'undelivered'
  | 'processing'
  | undefined;

@Component({
  selector: 'glxy-chat-message',
  imports: [CommonModule, MatIconModule, TranslateModule],
  templateUrl: './chat-message.component.html',
  styleUrls: ['./chat-message.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatMessageComponent {
  @HostBinding('class')
  get class() {
    return ['glxy-chat-message', this.messageStatusClass()];
  }

  readonly itemContent = input<'message' | 'attachment'>('message');
  readonly noBubble = input(false, { transform: booleanAttribute });
  readonly isTyping = input(false, { transform: booleanAttribute });
  readonly messageText = input<string | undefined>();
  readonly messageStatus = input<ChatMessageStatus>();
  readonly messageTime = input<string | null | undefined>();
  readonly messageIsMarkdown = input(false, { transform: booleanAttribute });

  protected readonly messageStatusText = computed(() => {
    switch (this.messageStatus()) {
      case 'read':
        return 'GALAXY.CHAT.MESSAGE_STATUS.READ';
      case 'delivered':
        return 'GALAXY.CHAT.MESSAGE_STATUS.DELIVERED';
      case 'failed':
      case 'undelivered':
        return 'GALAXY.CHAT.MESSAGE_STATUS.UNDELIVERED';
      case 'sent':
        return 'GALAXY.CHAT.MESSAGE_STATUS.SENT';
      case 'sending':
      default:
        return '';
    }
  });

  protected readonly messageStatusClass = computed(() => {
    switch (this.messageStatus()) {
      case 'read':
        return 'read';
      case 'delivered':
        return 'delivered';
      case 'failed':
      case 'undelivered':
        return 'failed';
      case 'sent':
        return 'sent';
      case 'sending':
        return 'sending';
      default:
        return '';
    }
  });

  protected isMetadataPresented = signal(false);

  private eRef = inject(ElementRef);

  @ViewChild('bubbleElement') bubbleElement!: ElementRef;

  constructor() {
    effect(() => {
      // Trigger on messageText or isTyping changes, but check if element exists before updating
      const messageText = this.messageText();
      const isTyping = this.isTyping();

      if (messageText && !isTyping) {
        // Use setTimeout to ensure the view has been initialized
        setTimeout(() => {
          if (this.bubbleElement) {
            this.updateMessageContent();
          }
        });
      }
    });
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    // Check if there is any selected text
    const selectedText = window.getSelection()?.toString();
    if (selectedText) {
      return;
    }

    // Check if metadata is being clicked
    if (this.eRef.nativeElement.querySelector('.metadata')?.contains(event.target)) {
      return;
    }

    // Check if the target is inside the element and not a link
    const isLink = event.target instanceof HTMLAnchorElement;
    const isInElement = this.eRef.nativeElement.contains(event.target);
    if (!isLink && isInElement) {
      this.isMetadataPresented.update((value) => !value);
    } else {
      if (this.itemContent() === 'message') {
        this.isMetadataPresented.set(false);
      }
    }
  }

  private updateMessageContent() {
    const messageText = this.messageText();
    if (messageText && !this.isTyping()) {
      let allowedAttr: string[] = [];
      const isSafeTarget = messageText.includes('target="_blank"') && messageText.includes('rel="noopener noreferrer"');
      if (isSafeTarget) {
        allowedAttr = ['target', 'rel'];
      }
      this.bubbleElement.nativeElement.innerHTML = DOMPurify.sanitize(messageText, { ADD_ATTR: allowedAttr });
    }
  }
}
